import React from "react";
import DOMPurify from "dompurify";
import axios from "axios";
import config from "../config";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { LazyLoadImage } from "react-lazy-load-image-component";
import placeholderimage from "../image/placeholder.jpg";
import noimage from "../image/noimage.jpg";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

const SITE_URL = "https://meragym.com";
const PREVIEW_PHOTOS = 5;

// "00:00" / "00.00" / empty are the default "unset" values
const hasHours = (t) => !!t && t !== "00:00" && t !== "00.00";

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const truncate = (text, max) =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

// Visitor details are remembered (browser-wide, so they carry across gyms and
// tabs) for 30 days after the first contact form is filled in.
const VISITOR_KEY = "mg_contact_visitor";
const VISITOR_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const readVisitor = () => {
  try {
    const v = JSON.parse(localStorage.getItem(VISITOR_KEY) || "null");
    if (
      v &&
      typeof v.name === "string" &&
      v.name.trim().length >= 2 &&
      /^\d{10}$/.test(v.phone || "") &&
      Date.now() - (v.savedAt || 0) < VISITOR_TTL_MS
    ) {
      return { name: v.name.trim(), phone: v.phone };
    }
  } catch (e) {
    /* storage unavailable or corrupt — treat as no saved visitor */
  }
  return null;
};

const saveVisitor = (v) => {
  try {
    localStorage.setItem(VISITOR_KEY, JSON.stringify({ ...v, savedAt: Date.now() }));
  } catch (e) {
    /* ignore */
  }
};

const clearVisitor = () => {
  try {
    localStorage.removeItem(VISITOR_KEY);
  } catch (e) {
    /* ignore */
  }
};

// Section heading with a tinted icon badge
const SectionTitle = ({ icon, tone, children }) => (
  <h2 className="gd-h2">
    <span className={`gd-ico gd-ico--${tone}`} aria-hidden="true">
      <i className={`bi ${icon}`}></i>
    </span>
    <span>{children}</span>
  </h2>
);

export default function Detail() {
  const [businessDetail, bussinessDetailState] = useState([]);
  const [businessDetailGallery, businessDetailGalleryState] = useState([]);
  const [planDetail, planDetailState] = useState([]);
  const [enquiry, setEnquiry] = useState(() => ({
    name: "",
    phone: "",
    message: "",
    ...(readVisitor() || {}),
  }));
  const [enquiryStatus, setEnquiryStatus] = useState({ type: "", text: "" });
  const [sending, setSending] = useState(false);
  // contact gate popup: { type: "call" | "whatsapp" | "email" } or null
  const [contactModal, setContactModal] = useState(null);
  const [visitor, setVisitor] = useState(() => readVisitor());
  const [contactForm, setContactForm] = useState(() => readVisitor() || { name: "", phone: "" });
  const [contactStatus, setContactStatus] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${config.apiUrl}/business-detail/${params.slug}`)
      .then((res) => {
        bussinessDetailState(res.data.business_detail);
        businessDetailGalleryState(res.data.business_detail.gallery || []);
        planDetailState(res.data.business_detail.plan_details || []);
      })
      .catch(() => {
        navigate("/error");
      });
  }, [params.slug, navigate]);

  // Returns true if the gym is currently open based on open_time / close_time
  const isOpenNow = (openTime, closeTime) => {
    if (!openTime || !closeTime) return null;
    const clean = (t) => t.replace(/[^0-9:]/g, "").trim();
    const toMinutes = (t) => {
      const parts = clean(t).split(":");
      if (parts.length < 2) return null;
      return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
    };
    const now = new Date();
    const currentMins = now.getHours() * 60 + now.getMinutes();
    const openMins = toMinutes(openTime);
    const closeMins = toMinutes(closeTime);
    if (openMins === null || closeMins === null) return null;
    if (openMins === 0 && closeMins === 0) return null; // default/unset
    return currentMins >= openMins && currentMins <= closeMins;
  };

  const gymIsOpen = isOpenNow(businessDetail.open_time, businessDetail.close_time);

  const carouselResponsive = {
    superLargeDesktop: { breakpoint: { max: 4000, min: 1921 }, items: 1, slidesToSlide: 1 },
    desktop: { breakpoint: { max: 1920, min: 1025 }, items: 1, slidesToSlide: 1 },
    tablet: { breakpoint: { max: 1024, min: 769 }, items: 1, slidesToSlide: 1 },
    mobile: { breakpoint: { max: 768, min: 0 }, items: 1, slidesToSlide: 1 },
  };

  // ---- derived data -------------------------------------------------------
  const name = businessDetail.name || "";
  const city = businessDetail.city || "";
  const hasPhone = !!businessDetail.has_phone;
  const hasEmail = !!businessDetail.has_email;
  const fullAddress = [
    businessDetail.area,
    businessDetail.address,
    businessDetail.pincode,
  ]
    .filter(Boolean)
    .join(", ");
  const mapQuery =
    businessDetail.latitude && businessDetail.longitude
      ? `${businessDetail.latitude},${businessDetail.longitude}`
      : [name, fullAddress, city].filter(Boolean).join(", ");
  const mapEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;
  const mapDirectionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapQuery)}`;
  const cityPath = city ? encodeURIComponent(city.toLowerCase()) : "";
  const categories = businessDetail.category || [];
  const amenities = businessDetail.amenity || [];
  const showHours =
    hasHours(businessDetail.open_time) && hasHours(businessDetail.close_time);
  const canonicalUrl = businessDetail.slug
    ? `${SITE_URL}/${businessDetail.slug}`
    : undefined;
  const coverImage =
    businessDetail.cover_image || businessDetailGallery[0] || noimage;
  const shareImage = businessDetail.cover_image || businessDetail.thumbnail_image;
  const aboutText = stripHtml(businessDetail.about);

  const seoTitle =
    businessDetail.meta_title ||
    (name ? `${name} — Gym in ${city} | MeraGym` : "Gym Details | MeraGym");
  const seoDescription =
    businessDetail.meta_description ||
    (name
      ? truncate(
          `${name} in ${city}. ${aboutText || `Check membership plans, facilities, photos and contact details of ${name} on MeraGym.`}`,
          160
        )
      : "Find gym details, membership plans and working hours on MeraGym.");

  const faqs = [];
  if (name && (fullAddress || city)) {
    faqs.push({
      q: `Where is ${name} located?`,
      a: `${name} is located at ${[fullAddress, city].filter(Boolean).join(", ")}.`,
    });
  }
  if (name && showHours) {
    faqs.push({
      q: `What are the timings of ${name}?`,
      a: `${name} is open from ${businessDetail.open_time} to ${businessDetail.close_time}.`,
    });
  }
  if (name && categories.length > 0) {
    faqs.push({
      q: `What workouts are available at ${name}?`,
      a: `${name} offers ${categories.map((c) => c.name).join(", ")}.`,
    });
  }
  if (name && amenities.length > 0) {
    faqs.push({
      q: `What facilities does ${name} provide?`,
      a: `Facilities include ${amenities.map((a) => a.name).join(", ")}.`,
    });
  }
  if (name && planDetail.length > 0) {
    faqs.push({
      q: `What are the membership fees at ${name}?`,
      a: `Membership plans: ${planDetail
        .map((p) => `${p.plan_name} – Rs ${p.plan_amount}`)
        .join("; ")}.`,
    });
  }
  if (name && hasPhone) {
    faqs.push({
      q: `How can I contact ${name}?`,
      a: `Use the Call or WhatsApp buttons on this page to get in touch with ${name} directly.`,
    });
  }

  const jsonLd = name
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "ExerciseGym",
            "@id": `${canonicalUrl}#gym`,
            name,
            url: canonicalUrl,
            description: seoDescription,
            image: [shareImage, ...businessDetailGallery.slice(0, 4)].filter(Boolean),
            address: {
              "@type": "PostalAddress",
              streetAddress: [businessDetail.area, businessDetail.address]
                .filter(Boolean)
                .join(", "),
              addressLocality: city,
              postalCode: businessDetail.pincode || undefined,
              addressCountry: "IN",
            },
            geo:
              businessDetail.latitude && businessDetail.longitude
                ? {
                    "@type": "GeoCoordinates",
                    latitude: businessDetail.latitude,
                    longitude: businessDetail.longitude,
                  }
                : undefined,
            sameAs: [
              businessDetail.facebook_link,
              businessDetail.instagram_link,
              businessDetail.twitter_link,
              businessDetail.youtube_link,
              businessDetail.linkedin_link,
            ].filter(Boolean),
            amenityFeature: amenities.map((a) => ({
              "@type": "LocationFeatureSpecification",
              name: a.name,
              value: true,
            })),
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Gyms", item: `${SITE_URL}/gyms` },
              ...(city
                ? [
                    {
                      "@type": "ListItem",
                      position: 3,
                      name: `Gyms in ${city}`,
                      item: `${SITE_URL}/${cityPath}/gym`,
                    },
                  ]
                : []),
              {
                "@type": "ListItem",
                position: city ? 4 : 3,
                name,
                item: canonicalUrl,
              },
            ],
          },
          ...(faqs.length > 0
            ? [
                {
                  "@type": "FAQPage",
                  mainEntity: faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                },
              ]
            : []),
        ],
      }
    : null;

  // ---- enquiry form -------------------------------------------------------
  const handleEnquiryChange = (e) => {
    const { name: field, value } = e.target;
    setEnquiry((prev) => ({ ...prev, [field]: value }));
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(enquiry.phone)) {
      setEnquiryStatus({ type: "error", text: "Please enter a valid 10-digit mobile number." });
      return;
    }
    setSending(true);
    setEnquiryStatus({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.apiUrl}/member-enquiry`, {
        name: enquiry.name,
        phone: enquiry.phone,
        city: city || "NA",
        interested_area: businessDetail.area || name,
        message: `[${name}] ${enquiry.message || "I would like to know more about membership plans."}`,
      });
      setEnquiryStatus({
        type: "success",
        text: res.data.message || "Thanks! The gym will contact you shortly.",
      });
      setEnquiry({ name: "", phone: "", message: "" });
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      const first = apiErrors ? Object.values(apiErrors)[0]?.[0] : null;
      setEnquiryStatus({
        type: "error",
        text: first || "Could not send your enquiry. Please try again or call the gym.",
      });
    } finally {
      setSending(false);
    }
  };

  // Smooth-scroll to an in-page section (offset comes from scroll-margin-top)
  const scrollToSection = (e, id) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    if (window.history?.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  // ---- contact gate ---------------------------------------------------------
  // Records the lead for this gym and then triggers the call / WhatsApp / email.
  // `waTab` must be a window opened synchronously inside the click handler.
  const sendLead = async (type, who, waTab) => {
    const res = await axios.post(`${config.apiUrl}/gym-contact-lead`, {
      slug: businessDetail.slug,
      name: who.name,
      phone: who.phone,
      contact_type: type,
      page_url: window.location.href,
    });
    saveVisitor(who);
    setVisitor(who);
    setEnquiry((prev) => ({ ...prev, name: prev.name || who.name, phone: prev.phone || who.phone }));

    const { phone, email } = res.data.data;
    if (type === "call") {
      window.location.href = `tel:${phone}`;
    } else if (type === "email") {
      window.location.href = `mailto:${email}`;
    } else {
      const waNumber = phone.length === 10 ? `91${phone}` : phone;
      const text = encodeURIComponent(
        `Hi ${name}, I'm ${who.name}. I found your gym on MeraGym (meragym.com) and I'm interested in joining. ` +
          `Please share the membership plans, fees and timings.\n\n` +
          `Your MeraGym listing: ${SITE_URL}/${businessDetail.slug}`
      );
      const url = `https://wa.me/${waNumber}?text=${text}`;
      if (waTab) waTab.location.href = url;
      else window.open(url, "_blank");
    }
  };

  const errorText = (err) => {
    const apiErrors = err.response?.data?.errors;
    const first = apiErrors ? Object.values(apiErrors)[0]?.[0] : null;
    return (
      first ||
      err.response?.data?.message ||
      "Something went wrong. Please try again in a moment."
    );
  };

  const showContactForm = (type, message = "") => {
    setContactForm(readVisitor() || { name: "", phone: "" });
    setContactStatus(message);
    setContactModal({ type });
  };

  // Button click: skip the popup when the visitor is already known.
  const openContact = async (type) => {
    if (contactSending) return;
    const known = readVisitor(); // re-read so details saved on another gym/tab are picked up
    if (!known) {
      showContactForm(type);
      return;
    }
    const waTab = type === "whatsapp" ? window.open("", "_blank") : null;
    setContactSending(true);
    try {
      await sendLead(type, known, waTab);
    } catch (err) {
      if (waTab) waTab.close();
      // Saved details were rejected (or the request failed): ask again.
      if (err.response?.status === 422 && err.response?.data?.errors) clearVisitor();
      showContactForm(type, errorText(err));
    } finally {
      setContactSending(false);
    }
  };

  const changeVisitor = () => {
    clearVisitor();
    setVisitor(null);
    setContactForm({ name: "", phone: "" });
    setContactStatus("");
    setContactModal({ type: "call", detailsOnly: true });
  };

  const closeContact = () => {
    if (!contactSending) setContactModal(null);
  };

  useEffect(() => {
    if (!contactModal) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !contactSending) setContactModal(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [contactModal, contactSending]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const type = contactModal.type;
    const who = { name: contactForm.name.trim(), phone: contactForm.phone };
    if (who.name.length < 2) {
      setContactStatus("Please enter your name.");
      return;
    }
    if (!/^\d{10}$/.test(who.phone)) {
      setContactStatus("Please enter a valid 10-digit mobile number.");
      return;
    }
    // "Change details" only updates the saved visitor; no call is placed.
    if (contactModal.detailsOnly) {
      saveVisitor(who);
      setVisitor(who);
      setContactModal(null);
      return;
    }
    // Open the tab now (inside the click) so the browser doesn't block it
    // after the async request; it is pointed at WhatsApp once we have the number.
    const waTab = type === "whatsapp" ? window.open("", "_blank") : null;
    setContactSending(true);
    setContactStatus("");
    try {
      await sendLead(type, who, waTab);
      setContactModal(null);
    } catch (err) {
      if (waTab) waTab.close();
      setContactStatus(errorText(err));
    } finally {
      setContactSending(false);
    }
  };

  const contactCopy = {
    call: { title: "Call the gym", icon: "bi-telephone-fill", tone: "green", cta: "Call now" },
    whatsapp: { title: "WhatsApp the gym", icon: "bi-whatsapp", tone: "green", cta: "Continue to WhatsApp" },
    email: { title: "Email the gym", icon: "bi-envelope-fill", tone: "amber", cta: "Send email" },
  };

  const openGallery = businessDetailGallery.length > 0;
  const previewPhotos = businessDetailGallery.slice(0, PREVIEW_PHOTOS);
  const extraPhotos = businessDetailGallery.length - PREVIEW_PHOTOS;

  const sectionNav = [
    { id: "overview", label: "Overview", icon: "bi-info-circle-fill", show: true },
    { id: "workouts", label: "Workouts", icon: "bi-lightning-charge-fill", show: categories.length > 0 },
    { id: "amenities", label: "Amenities", icon: "bi-stars", show: amenities.length > 0 },
    { id: "photos", label: "Photos", icon: "bi-camera-fill", show: openGallery },
    { id: "membership", label: "Membership", icon: "bi-award-fill", show: true },
    { id: "location", label: "Location", icon: "bi-geo-alt-fill", show: true },
    { id: "faq", label: "FAQs", icon: "bi-question-circle-fill", show: faqs.length > 0 },
  ].filter((s) => s.show);

  const socialLinks = [
    { href: businessDetail.facebook_link, icon: "bi-facebook", label: "Facebook" },
    { href: businessDetail.instagram_link, icon: "bi-instagram", label: "Instagram" },
    { href: businessDetail.twitter_link, icon: "bi-twitter", label: "Twitter" },
    { href: businessDetail.youtube_link, icon: "bi-youtube", label: "YouTube" },
    { href: businessDetail.linkedin_link, icon: "bi-linkedin", label: "LinkedIn" },
  ].filter((s) => s.href);

  return (
    <>
      <div className="detail-container-fluid gd-page">
        <HelmetProvider>
          <Helmet>
            <title>{seoTitle}</title>
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta
              name="keywords"
              content={
                businessDetail.meta_keyword ||
                (name
                  ? `${name}, gym in ${city}, fitness center ${city}`
                  : "gym, fitness center, meragym")
              }
            />
            <meta name="description" content={seoDescription} />
            {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
            <meta property="og:type" content="business.business" />
            <meta property="og:site_name" content="MeraGym" />
            {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
            <meta property="og:title" content={seoTitle} />
            <meta property="og:description" content={seoDescription} />
            {shareImage && <meta property="og:image" content={shareImage} />}
            {shareImage && name && (
              <meta property="og:image:alt" content={`${name} gym in ${city}`} />
            )}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seoTitle} />
            <meta name="twitter:description" content={seoDescription} />
            {shareImage && <meta name="twitter:image" content={shareImage} />}
            {jsonLd && (
              <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            )}
          </Helmet>
        </HelmetProvider>

        <div className="container">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="gd-breadcrumb">
            <ol>
              <li>
                <Link to="/">
                  <i className="bi bi-house-door-fill me-1" aria-hidden="true"></i>Home
                </Link>
              </li>
              <li>
                <Link to="/gyms">Gyms</Link>
              </li>
              {city && (
                <li>
                  <Link to={`/${cityPath}/gym`}>Gyms in {city}</Link>
                </li>
              )}
              <li aria-current="page">{name}</li>
            </ol>
          </nav>

          <div className="row detail-container-row g-3">
            <main className="col-12 col-lg-8">
              {/* Hero / summary */}
              <article className="card gd-card gd-hero" id="overview-section">
                <LazyLoadImage
                  src={coverImage}
                  placeholderSrc={placeholderimage}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = noimage;
                  }}
                  className="gd-cover"
                  alt={name ? `${name} - gym in ${city}` : "Gym cover image"}
                />
                <div className="card-body gd-hero-body">
                  <div className="gd-title-row">
                    <h1 className="detail-gym-name gd-title">{name}</h1>
                    {gymIsOpen !== null && (
                      <span
                        className={`mg-open-badge ${gymIsOpen ? "mg-open-badge--open" : "mg-open-badge--closed"}`}
                      >
                        <i className={`bi ${gymIsOpen ? "bi-circle-fill" : "bi-circle"} me-1`}></i>
                        {gymIsOpen ? "Open Now" : "Closed"}
                      </span>
                    )}
                  </div>

                  <p className="gd-location">
                    <i className="bi bi-geo-alt-fill" aria-hidden="true"></i>
                    <span>{[businessDetail.area, city].filter(Boolean).join(", ")}</span>
                  </p>

                  {categories.length > 0 && (
                    <ul className="gd-chips" aria-label="Workout types">
                      {categories.map((cat) => (
                        <li key={cat.id}>
                          <Link to={city ? `/${cityPath}/${cat.slug}` : `/gyms/${cat.slug}`}>
                            <i className="bi bi-lightning-charge-fill" aria-hidden="true"></i>
                            {cat.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}

                  <p className="gd-actions-label">Contact this gym</p>
                  <div className="gd-actions gd-actions--gym">
                    {hasPhone && (
                      <button
                        type="button"
                        className="gd-action gd-action--primary"
                        onClick={() => openContact("call")}
                      >
                        <i className="bi bi-telephone-fill" aria-hidden="true"></i> Call
                      </button>
                    )}
                    {hasPhone && (
                      <button
                        type="button"
                        className="gd-action gd-action--whatsapp"
                        onClick={() => openContact("whatsapp")}
                      >
                        <i className="bi bi-whatsapp" aria-hidden="true"></i> WhatsApp
                      </button>
                    )}
                    <a
                      href="#enquiry"
                      className="gd-action gd-action--amber"
                      onClick={(e) => scrollToSection(e, "enquiry")}
                    >
                      <i className="bi bi-chat-dots-fill" aria-hidden="true"></i> Enquire
                    </a>
                    {socialLinks.map((s) => (
                      <a
                        key={s.label}
                        href={s.href}
                        className={`gd-action gd-action--icon gd-brand-${s.icon.replace("bi-", "")}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label={`${name} on ${s.label}`}
                      >
                        <i className={`bi ${s.icon}`} aria-hidden="true"></i>
                      </a>
                    ))}
                  </div>

                  <dl className="gd-facts">
                    {fullAddress && (
                      <div>
                        <dt>
                          <span className="gd-ico gd-ico--sm gd-ico--red"><i className="bi bi-geo-alt-fill"></i></span> Address
                        </dt>
                        <dd>
                          <address>{[fullAddress, city].filter(Boolean).join(", ")}</address>
                        </dd>
                      </div>
                    )}
                    {showHours && (
                      <div>
                        <dt>
                          <span className="gd-ico gd-ico--sm gd-ico--amber"><i className="bi bi-clock-fill"></i></span> Working hours
                        </dt>
                        <dd>
                          {businessDetail.open_time} – {businessDetail.close_time}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </article>

              {/* Section jump-links */}
              <nav className="gd-jump" aria-label="Page sections">
                {sectionNav.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}-section`}
                    onClick={(e) => scrollToSection(e, `${s.id}-section`)}
                  >
                    <i className={`bi ${s.icon}`} aria-hidden="true"></i>
                    {s.label}
                  </a>
                ))}
              </nav>

              {/* About */}
              {aboutText && (
                <section className="card gd-card" id="about-section">
                  <div className="card-body">
                    <SectionTitle icon="bi-info-circle-fill" tone="blue">About {name}</SectionTitle>
                    <div
                      className="detail-about-content gd-about"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(businessDetail.about || ""),
                      }}
                    ></div>
                  </div>
                </section>
              )}

              {/* Workouts */}
              {categories.length > 0 && (
                <section className="card gd-card" id="workouts-section">
                  <div className="card-body">
                    <SectionTitle icon="bi-lightning-charge-fill" tone="red">Workouts at {name}</SectionTitle>
                    <ul className="gd-tiles">
                      {categories.map((cat) => (
                        <li key={cat.id}>
                          <span className="gd-tile-ico gd-tile-ico--red">
                            <i className="bi bi-lightning-charge-fill" aria-hidden="true"></i>
                            {cat.image && (
                              <img
                                src={cat.image}
                                alt=""
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            )}
                          </span>
                          <span>{cat.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              )}

              {/* Amenities */}
              {amenities.length > 0 && (
                <section className="card gd-card" id="amenities-section">
                  <div className="card-body">
                    <SectionTitle icon="bi-stars" tone="amber">Amenities &amp; facilities</SectionTitle>
                    <ul className="gd-tiles">
                      {amenities.map((amenity) => (
                        <li key={amenity.id || amenity.name}>
                          <span className="gd-tile-ico gd-tile-ico--green">
                            <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                            {amenity.image && (
                              <img
                                src={amenity.image}
                                alt=""
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            )}
                          </span>
                          <span>{amenity.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              )}

              {/* Photos */}
              {openGallery && (
                <section className="card gd-card" id="photos-section">
                  <div className="card-body">
                    <div className="gd-section-head">
                      <SectionTitle icon="bi-camera-fill" tone="purple">Photos of {name}</SectionTitle>
                      <button
                        type="button"
                        className="gd-link-btn"
                        data-bs-toggle="modal"
                        data-bs-target="#GalleryModal"
                      >
                        <i className="bi bi-images me-1" aria-hidden="true"></i>
                        View all {businessDetailGallery.length}
                      </button>
                    </div>
                    <div className="gd-gallery">
                      {previewPhotos.map((img, index) => (
                        <button
                          type="button"
                          key={index}
                          className="gd-gallery-item"
                          data-bs-toggle="modal"
                          data-bs-target="#GalleryModal"
                          aria-label={`Open photo ${index + 1} of ${name}`}
                        >
                          <LazyLoadImage
                            src={img || noimage}
                            alt={`${name || "Gym"} photo ${index + 1}`}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = noimage;
                            }}
                          />
                          {index === PREVIEW_PHOTOS - 1 && extraPhotos > 0 && (
                            <span className="gd-gallery-more">+{extraPhotos}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* Gallery modal */}
              {openGallery && (
                <div
                  className="modal fade ps-0 modal-gallery"
                  id="GalleryModal"
                  aria-hidden="true"
                  aria-label={`${name} photo gallery`}
                  tabIndex="-1"
                >
                  <div className="modal-dialog modal-dialog-centered modal-fullscreen">
                    <div className="modal-content">
                      <div className="modal-body bg-glossy">
                        <div className="container position-relative">
                          <button
                            type="button"
                            className="button-close"
                            data-bs-dismiss="modal"
                            aria-label="Close gallery"
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                          <div className="row">
                            <div className="col-sm-12 position-relative px-0">
                              <Carousel
                                responsive={carouselResponsive}
                                autoPlay={false}
                                infinite={true}
                              >
                                {businessDetailGallery.map((img, index) => (
                                  <LazyLoadImage
                                    key={index}
                                    src={img || noimage}
                                    className="gym-gallery"
                                    alt={`${name || "Gym"} photo ${index + 1}`}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = noimage;
                                    }}
                                  />
                                ))}
                              </Carousel>
                              <div className="text-white text-end mt-2">
                                <h6>{businessDetailGallery.length} photos</h6>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Membership */}
              <section className="card gd-card" id="membership-section">
                <div className="card-body">
                  <SectionTitle icon="bi-award-fill" tone="green">Membership plans &amp; fees</SectionTitle>
                  {planDetail.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table gd-table">
                        <thead>
                          <tr>
                            <th scope="col">Plan</th>
                            <th scope="col" className="text-end">
                              Fee
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {planDetail.map((plan) => (
                            <tr key={plan.id}>
                              <th scope="row">
                                <i className="bi bi-patch-check-fill gd-plan-ico" aria-hidden="true"></i>
                                {plan.plan_name}
                              </th>
                              <td className="text-end gd-price">Rs {plan.plan_amount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="gd-muted mb-3">
                      {name} hasn&apos;t listed its membership fees yet. Send an
                      enquiry or call the gym to get the latest plans and offers.
                    </p>
                  )}
                  <a
                    href="#enquiry"
                    className="gd-action gd-action--primary"
                    onClick={(e) => scrollToSection(e, "enquiry")}
                  >
                    <i className="bi bi-chat-dots-fill" aria-hidden="true"></i> Ask about
                    membership
                  </a>
                </div>
              </section>

              {/* Location + contact */}
              <section className="card gd-card" id="location-section">
                <div className="card-body">
                  <SectionTitle icon="bi-geo-alt-fill" tone="red">Location &amp; contact</SectionTitle>
                  <p className="gd-muted">
                    {name} is in {[businessDetail.area, city].filter(Boolean).join(", ")}
                    {businessDetail.pincode ? ` (${businessDetail.pincode})` : ""}.
                  </p>
                  <div className="ratio ratio-16x9 gd-map">
                    <iframe
                      src={mapEmbedUrl}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Map showing the location of ${name}`}
                    ></iframe>
                  </div>
                  <div className="gd-actions mt-3">
                    <a
                      href={mapDirectionsUrl}
                      className="gd-action gd-action--primary"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="bi bi-sign-turn-right-fill" aria-hidden="true"></i> Get directions
                    </a>
                    {hasPhone && (
                      <button
                        type="button"
                        className="gd-action gd-action--green"
                        onClick={() => openContact("call")}
                      >
                        <i className="bi bi-telephone-fill" aria-hidden="true"></i> Call the gym
                      </button>
                    )}
                    {hasEmail && (
                      <button
                        type="button"
                        className="gd-action gd-action--amber"
                        onClick={() => openContact("email")}
                      >
                        <i className="bi bi-envelope-fill" aria-hidden="true"></i> Email the gym
                      </button>
                    )}
                  </div>
                  {visitor && (hasPhone || hasEmail) && (
                    <p className="gd-visitor">
                      <i className="bi bi-person-check-fill" aria-hidden="true"></i>
                      Contacting as <strong>{visitor.name}</strong> ({visitor.phone}) ·{" "}
                      <button type="button" className="gd-link-btn" onClick={changeVisitor}>
                        Change
                      </button>
                    </p>
                  )}
                </div>
              </section>

              {/* FAQs (also exposed as FAQPage JSON-LD) */}
              {faqs.length > 0 && (
                <section className="card gd-card" id="faq-section">
                  <div className="card-body">
                    <SectionTitle icon="bi-question-circle-fill" tone="blue">Frequently asked questions</SectionTitle>
                    <div className="gd-faq">
                      {faqs.map((f, i) => (
                        <details key={i} open={i === 0}>
                          <summary>{f.q}</summary>
                          <p>{f.a}</p>
                        </details>
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </main>

            <aside className="col-12 col-lg-4">
              <div className="gd-sidebar">
                {/* WhatsApp CTA — highest conversion action */}
                {hasPhone && (
                  <div className="mg-whatsapp-card mb-3">
                    <p className="mg-contact-head">
                      <i className="bi bi-building me-1" aria-hidden="true"></i>
                      Contact {name || "this gym"}
                    </p>
                    <button
                      type="button"
                      className="mg-whatsapp-btn"
                      onClick={() => openContact("whatsapp")}
                    >
                      <i className="bi bi-whatsapp me-2"></i>
                      WhatsApp the Gym
                    </button>
                    <button
                      type="button"
                      className="mg-call-btn mt-2"
                      onClick={() => openContact("call")}
                    >
                      <i className="bi bi-telephone-fill me-2"></i>
                      Call the Gym
                    </button>
                  </div>
                )}

                <div className="card detail-inquiry-card gd-card" id="enquiry">
                  <div className="card-body">
                    <SectionTitle icon="bi-chat-dots-fill" tone="red">Enquire about this gym</SectionTitle>
                    <p className="gd-muted">
                      Request membership details and offers for {name || "this gym"}.
                      The MeraGym team will connect you with the gym.
                    </p>
                    <form className="request-form" onSubmit={handleEnquirySubmit} noValidate>
                      <label htmlFor="enq-name" className="visually-hidden">
                        Your name
                      </label>
                      <div className="input-group mt-3 gd-input">
                      <span className="input-group-text" aria-hidden="true">
                        <i className="bi bi-person-fill"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="enq-name"
                        name="name"
                        placeholder="Your name"
                        autoComplete="name"
                        value={enquiry.name}
                        onChange={handleEnquiryChange}
                        required
                      />
                      </div>
                      <label htmlFor="enq-phone" className="visually-hidden">
                        Mobile number
                      </label>
                      <div className="input-group mt-3 gd-input">
                      <span className="input-group-text" aria-hidden="true">
                        <i className="bi bi-telephone-fill"></i>
                      </span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        className="form-control"
                        id="enq-phone"
                        name="phone"
                        placeholder="10-digit mobile number"
                        autoComplete="tel-national"
                        value={enquiry.phone}
                        onChange={handleEnquiryChange}
                        required
                      />
                      </div>
                      <label htmlFor="enq-message" className="visually-hidden">
                        Your message
                      </label>
                      <textarea
                        className="form-control mt-3"
                        rows="3"
                        id="enq-message"
                        name="message"
                        placeholder="I'd like to know about membership plans…"
                        value={enquiry.message}
                        onChange={handleEnquiryChange}
                      />
                      {enquiryStatus.text && (
                        <p
                          role="status"
                          className={`gd-form-msg gd-form-msg--${enquiryStatus.type}`}
                        >
                          {enquiryStatus.text}
                        </p>
                      )}
                      <button
                        type="submit"
                        className="btn btn-danger btn-border-0 w-100 mt-3"
                        disabled={sending || !enquiry.name || !enquiry.phone}
                      >
                        <i className="bi bi-send-fill me-2" aria-hidden="true"></i>
                        {sending ? "Sending…" : "Send enquiry"}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* Contact gate popup */}
        {contactModal && (
          <div
            className="gd-modal-backdrop"
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeContact();
            }}
          >
            <div
              className="gd-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="gd-contact-title"
            >
              <button
                type="button"
                className="gd-modal-close"
                onClick={closeContact}
                aria-label="Close"
              >
                <i className="bi bi-x-lg"></i>
              </button>
              <div
                className={`gd-ico gd-ico--lg gd-ico--${contactCopy[contactModal.type].tone}`}
                aria-hidden="true"
              >
                <i className={`bi ${contactCopy[contactModal.type].icon}`}></i>
              </div>
              <h2 id="gd-contact-title" className="gd-modal-title">
                {contactModal.detailsOnly ? "Your contact details" : contactCopy[contactModal.type].title}
              </h2>
              <p className="gd-muted">
                {contactModal.detailsOnly ? (
                  <>
                    We&apos;ll use these details whenever you contact a gym, so you
                    won&apos;t be asked again.
                  </>
                ) : (
                  <>
                    Share your name and mobile number so <strong>{name}</strong> can
                    reach you back. We&apos;ll connect you right away — and
                    remember you for other gyms.
                  </>
                )}
              </p>
              <form onSubmit={handleContactSubmit} noValidate>
                <div className="input-group gd-input mb-3">
                  <span className="input-group-text" aria-hidden="true">
                    <i className="bi bi-person-fill"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Your name"
                    aria-label="Your name"
                    autoComplete="name"
                    autoFocus
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  />
                </div>
                <div className="input-group gd-input">
                  <span className="input-group-text" aria-hidden="true">
                    <i className="bi bi-telephone-fill"></i>
                  </span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    className="form-control"
                    placeholder="10-digit mobile number"
                    aria-label="Your mobile number"
                    autoComplete="tel-national"
                    value={contactForm.phone}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        phone: e.target.value.replace(/\D/g, ""),
                      })
                    }
                  />
                </div>
                {contactStatus && (
                  <p role="alert" className="gd-form-msg gd-form-msg--error">
                    {contactStatus}
                  </p>
                )}
                <button
                  type="submit"
                  className="btn btn-danger w-100 mt-3"
                  disabled={contactSending}
                >
                  <i className={`bi ${contactModal.detailsOnly ? "bi-check-circle-fill" : contactCopy[contactModal.type].icon} me-2`} aria-hidden="true"></i>
                  {contactSending
                    ? "Please wait…"
                    : contactModal.detailsOnly
                    ? "Save details"
                    : contactCopy[contactModal.type].cta}
                </button>
                <p className="gd-modal-note">
                  <i className="bi bi-shield-lock-fill me-1" aria-hidden="true"></i>
                  Your number is shared only with this gym.
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
